/**
 * Scan list table with status badges, progress, and cancel button.
 */

import DataTable, { type Column } from '@/components/common/DataTable';
import StatusBadge from '@/components/common/StatusBadge';
import { completedWithErrors, formatDate } from '@/utils/formatters';
import { XCircle, Trash2 } from 'lucide-react'; // Import Trash2 icon
import type { Scan } from '@/types/scan';
import { useDeleteScan } from '@/hooks/useScans'; // Import the delete hook

interface ScanListProps {
  scans: Scan[];
  onCancel: (scan: Scan) => void;
  /** Open the scan's details, including per-engine results. */
  onSelect?: (scan: Scan) => void;
  canManage: boolean;
}

export default function ScanList({ scans, onCancel, onSelect, canManage }: ScanListProps) {
  const deleteMutation = useDeleteScan(); // Initialize delete mutation

  const columns: Column<Scan>[] = [
    { key: 'name', header: 'Scan Name', sortable: true },
    {
      key: 'scan_type',
      header: 'Type',
      render: (item: Scan) => (
        <span className="text-xs font-medium uppercase text-gray-500">{item.scan_type}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: Scan) => <StatusBadge status={item.status} withErrors={completedWithErrors(item)} />,
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (item: Scan) => (
        <div className="flex items-center gap-2">
          <div className="h-2 w-20 overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-indigo-600 transition-all"
              style={{ width: `${item.progress}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">{item.progress}%</span>
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Started',
      sortable: true,
      render: (item: Scan) => <span className="text-xs">{formatDate(item.started_at ?? item.created_at)}</span>,
    },
  ];

  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      className: 'w-24 text-right', // Adjusted width for two buttons
      render: (item: Scan) => (
        <div className="flex justify-end gap-1"> {/* Flex container for buttons */}
          {(item.status === 'pending' || item.status === 'running') && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancel(item); }}
              className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
              title="Cancel scan"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm(`Are you sure you want to delete scan "${item.name}"?`)) {
                deleteMutation.mutate(item.id);
              }
            }}
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
            title="Delete scan"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    });
  }

  return (
    <DataTable
      columns={columns}
      data={scans}
      rowKey={(item) => item.id}
      onRowClick={onSelect}
      emptyMessage="No scans found. Create a scan to get started."
    />
  );
}

